import { Link } from "react-router-dom";

const sections = [
  { id: "start-here", label: "Start Here" },
  { id: "first-run", label: "Run Your First Model" },
  { id: "results", label: "Understand Results" },
  { id: "ai-help", label: "Using AI Suggestions" },
  { id: "glossary", label: "Simple ML Terms" },
  { id: "learning-path", label: "Beginner Learning Path" }, 
  { id: "helpful-links", label: "Helpful Links" },
];

const Resources = () => {
  const handleMobileChange = (e) => {
    const id = e.target.value;
    if (!id) return;

    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F0F9FF] via-[#F7FAFC] to-[#F8FAFC] text-[#0F172A] font-[Manrope]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* PAGE HEADER */}
        <header className="mb-6 sm:mb-8">
          <p className="text-xs sm:text-sm font-semibold tracking-wide text-[#0EA5E9] uppercase mb-2">
            Resources
          </p>

          <h1 className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-[#003153] via-[#0369A1] to-[#0EA5E9] bg-clip-text text-transparent mb-3">
            Learn DEEBug Step by Step
          </h1>

          <p className="text-sm sm:text-base text-[#64748B] max-w-3xl">
            New to machine learning? That is completely okay. This page explains
            how to use DEEBug in simple steps, without requiring advanced
            computer science or machine learning knowledge.
          </p>
        </header>

        {/* WELCOME CARD */}
        <section className="relative overflow-hidden bg-gradient-to-r from-[#E0F2FE] via-[#F0F9FF] to-[#ECFEFF] border border-[#BAE6FD] rounded-2xl p-5 sm:p-6 mb-8 shadow-sm">
          <h2 className="text-xl font-bold text-[#003153] mb-2">
            Welcome to DEEBug
          </h2>
          <p className="text-[#334155] text-sm sm:text-base">
            DEEBug helps you test your machine learning code, see how your model
            performed, and understand what you might improve next. You do not
            need to know every technical term before getting started.
          </p>
        </section>

        {/* MOBILE JUMP DROPDOWN */}
        <div className="lg:hidden sticky top-20 z-20 mb-6 bg-[#F7FAFC] pb-2">
          <label className="block text-xs font-semibold text-[#475569] mb-1">
            Jump to section
          </label>
          <select
            onChange={handleMobileChange}
            className="w-full text-sm border border-[#E2E8F0] rounded-lg px-3 py-2 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:border-[#0EA5E9]"
            defaultValue=""
          >
            <option value="" disabled>
              Select a section...
            </option>
            {sections.map((section) => (
              <option key={section.id} value={section.id}>
                {section.label}
              </option>
            ))}
          </select>
        </div>

        {/* DESKTOP GRID */}
        <div className="lg:grid lg:grid-cols-[260px,minmax(0,1fr)] lg:gap-10">
          {/* DESKTOP STICKY SIDEBAR */}
          <aside className="hidden lg:block">
            <div className="sticky top-24 bg-white/90 backdrop-blur border border-[#BAE6FD] rounded-2xl p-4 shadow-sm hover:shadow-md transition-shadow">
              <h2 className="text-sm font-semibold text-[#475569] mb-3">
                On this page
              </h2>

              <nav aria-label="Resources page sections">
                <ul className="space-y-1.5 text-sm text-[#334155]">
                  {sections.map((section) => (
                    <li key={section.id}>
                      <a
                        href={`#${section.id}`}
                        className="block px-2 py-1 rounded-md hover:text-[#0EA5E9] hover:bg-[#E0F2FE] transition"
                      >
                        {section.label}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          </aside>

          {/* MAIN CONTENT */}
          <main className="space-y-8 sm:space-y-10 text-[#334155] leading-relaxed">
            {/* Start Here */}
            <section
              id="start-here"
              className="bg-white border border-[#E2E8F0] border-t-4 border-t-[#0EA5E9] rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-6 sm:p-8 scroll-mt-32"
            >
              <h2 className="text-2xl font-bold text-[#003153] mb-4">
                Start Here
              </h2>

              <p className="mb-4">
                DEEBug is a learning-friendly tool that helps you run a machine
                learning model and understand what happened. Think of it as a
                helper that reviews your model results and gives suggestions for
                what you can try next.
              </p>

              <div className="grid sm:grid-cols-3 gap-4 mt-6">
                <div className="border border-[#BAE6FD] rounded-xl p-4 bg-gradient-to-br from-[#F0F9FF] to-white hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                  <div className="w-9 h-9 rounded-full bg-[#E0F2FE] text-[#0284C7] flex items-center justify-center font-bold text-sm mb-3">
                    1
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#0EA5E9] mb-2">
                    Add
                  </p>
                  <h3 className="font-semibold text-[#003153] mb-2">
                    1. Add your data
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Open a project and use the Code Editor to add or select your dataset.
                  </p>
                </div>

                <div className="border border-[#BAE6FD] rounded-xl p-4 bg-gradient-to-br from-[#E0F2FE] to-white hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                  <div className="w-9 h-9 rounded-full bg-[#BAE6FD] text-[#0369A1] flex items-center justify-center font-bold text-sm mb-3">
                    2
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#0284C7] mb-2">
                    Run
                  </p>
                  <h3 className="font-semibold text-[#003153] mb-2">
                    2. Run your model
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Choose a codebase from your projects and run your model in the editor.
                  </p>
                </div>

                <div className="border border-[#BAE6FD] rounded-xl p-4 bg-gradient-to-br from-[#DBEAFE] to-white hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                  <div className="w-9 h-9 rounded-full bg-[#BFDBFE] text-[#1D4ED8] flex items-center justify-center font-bold text-sm mb-3">
                    3
                  </div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-[#2563EB] mb-2">
                    Receive
                  </p>
                  <h3 className="font-semibold text-[#003153] mb-2">
                    3. Receive feedback
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    After running your model, review the output and AI suggestions in the editor.
                  </p>
                </div>
              </div>
            </section>

            {/* Run Your First Model */}
            <section
              id="first-run"
              className="bg-white border border-[#E2E8F0] border-t-4 border-t-[#0EA5E9] rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-6 sm:p-8 scroll-mt-32"
            >
              <h2 className="text-2xl font-bold text-[#003153] mb-4">
                Run Your First Model
              </h2>

              <p className="mb-4">
                Follow these steps if you are using DEEBug for the first time.
              </p>

              <ol className="list-decimal ml-5 space-y-3">
                <li>
                  <strong>Create an account.</strong> Sign up and log in so your
                  work can be saved.
                </li>
                <li>
                  <strong>
                    Use the{" "}
                    <Link
                      to="/projects"
                      className="text-[#0EA5E9] hover:text-[#0284C7] underline transition"
                    >
                      starter project
                    </Link>{" "}
                    or create a new one.
                  </strong>{" "}
                  A project keeps your code and dataset organized.
                </li>
                <li>
                  <strong>Upload a dataset.</strong> Start with a small CSV file
                  if you are learning.
                </li>
                <li>
                  <strong>Open your codebase.</strong> Use the editor to write
                  your code or start from a template.
                </li>
                <li>
                  <strong>Run the model.</strong> DEEBug will process your code
                  and show the result.
                </li>
                <li>
                  <strong>Read the feedback.</strong> Use the AI suggestions to
                  decide what to try next.
                </li>
              </ol>

              <div className="mt-5 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4">
                <p className="text-sm text-[#475569]">
                  Tip: If something does not work, read the error message first.
                  DEEBug is designed to help you understand what went wrong and
                  what to try next.
                </p>
              </div>
            </section>

            {/* Understand Results */}
            <section
              id="results"
              className="bg-gradient-to-br from-white to-[#F0F9FF] border border-[#BAE6FD] border-t-4 border-t-[#0EA5E9] rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-6 sm:p-8 scroll-mt-32"
            >
              <h2 className="text-2xl font-bold text-[#003153] mb-4">
                Understand Your Results
              </h2>

              <p className="mb-4">
                After you run a model, DEEBug may show numbers, messages, and
                feedback. You do not need to understand everything at once. Start
                with the basics below.
              </p>

              <div className="space-y-4">
                <div className="rounded-xl border border-[#BAE6FD] border-l-4 border-l-[#0EA5E9] bg-white/80 p-4 hover:shadow-sm transition-shadow">
                  <h3 className="font-semibold text-[#003153]">Accuracy</h3>
                  <p className="text-sm text-[#64748B]">
                    Accuracy tells you how often the model made the correct
                    prediction. Higher is usually better, but it is not the only
                    thing that matters.
                  </p>
                </div>

                <div className="rounded-xl border border-[#BAE6FD] border-l-4 border-l-[#0284C7] bg-white/80 p-4 hover:shadow-sm transition-shadow">
                  <h3 className="font-semibold text-[#003153]">Loss</h3>
                  <p className="text-sm text-[#64748B]">
                    Loss is a number that shows how far the model was from the
                    expected answer. Lower loss usually means the model is
                    improving.
                  </p>
                </div>

                <div className="rounded-xl border border-[#BAE6FD] border-l-4 border-l-[#0369A1] bg-white/80 p-4 hover:shadow-sm transition-shadow">
                  <h3 className="font-semibold text-[#003153]">Output</h3>
                  <p className="text-sm text-[#64748B]">
                    Output is what your code produced after running. This may
                    include results, messages, or errors.
                  </p>
                </div>
              </div>
            </section>

            {/* AI Help */}
            <section
              id="ai-help"
              className="bg-gradient-to-br from-white to-[#F0F9FF] border border-[#BAE6FD] border-t-4 border-t-[#0EA5E9] rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-6 sm:p-8 scroll-mt-32"
            >
              <h2 className="text-2xl font-bold text-[#003153] mb-4">
                Using AI Suggestions
              </h2>

              <p className="mb-4">
                DEEBug uses an AI assistant to review your model results and
                suggest possible improvements. These suggestions are meant to
                guide your learning, not replace your own testing.
              </p>

              <ul className="list-disc ml-5 space-y-2">
                <li>Read the suggestion carefully.</li>
                <li>Try one change at a time.</li>
                <li>Run the model again after making a change.</li>
                <li>Compare the new result with the previous result.</li>
                <li>Keep the change only if it improves your model.</li>
              </ul>

              <div className="mt-5 rounded-xl bg-[#FEFCE8] border border-[#FEF08A] p-4">
                <p className="text-sm text-[#713F12]">
                  Remember: AI suggestions can be helpful, but they may not
                  always be correct. Always test changes before trusting them.
                </p>
              </div>
            </section>

            {/* Glossary */}
            <section
              id="glossary"
              className="bg-gradient-to-br from-white to-[#F0F9FF] border border-[#BAE6FD] border-t-4 border-t-[#0EA5E9] rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-6 sm:p-8 scroll-mt-32"
            >
              <h2 className="text-2xl font-bold text-[#003153] mb-2">
                Simple ML Terms
              </h2>

              <p className="text-sm text-[#64748B] mb-6">
                These terms are grouped by the stage of a machine learning project
                where they are most commonly used.
              </p>

              <div className="space-y-8">
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#E0F2FE] text-[#0284C7] font-bold text-sm">
                      1
                    </span>
                    <div>
                      <h3 className="text-lg font-bold text-[#003153]">
                        Prepare Your Data
                      </h3>
                      <p className="text-sm text-[#64748B]">
                        Terms used when collecting and organizing information.
                      </p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="rounded-xl border border-[#A5F3FC] p-4 bg-[#ECFEFF] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <h4 className="font-semibold text-[#003153]">Dataset</h4>
                      <p className="text-sm text-[#64748B]">
                        The collection of information your model uses to learn and make predictions.
                      </p>
                    </div>

                    <div className="rounded-xl border border-[#A5F3FC] p-4 bg-[#ECFEFF] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <h4 className="font-semibold text-[#003153]">Feature</h4>
                      <p className="text-sm text-[#64748B]">
                        A piece of information the model uses, such as age, price, size, or category.
                      </p>
                    </div>

                    <div className="rounded-xl border border-[#A5F3FC] p-4 bg-[#ECFEFF] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <h4 className="font-semibold text-[#003153]">Label</h4>
                      <p className="text-sm text-[#64748B]">
                        The correct answer or outcome that a supervised model is trying to predict.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#DBEAFE] text-[#2563EB] font-bold text-sm">
                      2
                    </span>
                    <div>
                      <h3 className="text-lg font-bold text-[#003153]">
                        Train the Model
                      </h3>
                      <p className="text-sm text-[#64748B]">
                        Terms used while a model is learning patterns from data.
                      </p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="rounded-xl border border-[#BAE6FD] p-4 bg-[#F0F9FF] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <h4 className="font-semibold text-[#003153]">Model</h4>
                      <p className="text-sm text-[#64748B]">
                        The program that learns patterns from data and uses them to produce results.
                      </p>
                    </div>

                    <div className="rounded-xl border border-[#BAE6FD] p-4 bg-[#F0F9FF] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <h4 className="font-semibold text-[#003153]">Training</h4>
                      <p className="text-sm text-[#64748B]">
                        The process where the model studies examples and adjusts itself to find patterns.
                      </p>
                    </div>

                    <div className="rounded-xl border border-[#BAE6FD] p-4 bg-[#F0F9FF] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <h4 className="font-semibold text-[#003153]">Epoch</h4>
                      <p className="text-sm text-[#64748B]">
                        One complete pass through the training data, similar to one round of learning.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#BFDBFE] text-[#1D4ED8] font-bold text-sm">
                      3
                    </span>
                    <div>
                      <h3 className="text-lg font-bold text-[#003153]">
                        Test and Evaluate
                      </h3>
                      <p className="text-sm text-[#64748B]">
                        Terms used to check whether a model performs well.
                      </p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="rounded-xl border border-[#93C5FD] p-4 bg-[#DBEAFE] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <h4 className="font-semibold text-[#003153]">Testing Data</h4>
                      <p className="text-sm text-[#64748B]">
                        Data kept separate so you can check how well the trained model handles new examples.
                      </p>
                    </div>

                    <div className="rounded-xl border border-[#93C5FD] p-4 bg-[#DBEAFE] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <h4 className="font-semibold text-[#003153]">Metric</h4>
                      <p className="text-sm text-[#64748B]">
                        A number used to measure model performance, such as accuracy, precision, or recall.
                      </p>
                    </div>

                    <div className="rounded-xl border border-[#93C5FD] p-4 bg-[#DBEAFE] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <h4 className="font-semibold text-[#003153]">Baseline</h4>
                      <p className="text-sm text-[#64748B]">
                        A simple first result used as a reference when comparing improved models.
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#DBEAFE] text-[#1E40AF] font-bold text-sm">
                      4
                    </span>
                    <div>
                      <h3 className="text-lg font-bold text-[#003153]">
                        Review and Improve
                      </h3>
                      <p className="text-sm text-[#64748B]">
                        Terms used when reviewing output and fixing problems.
                      </p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="rounded-xl border border-[#60A5FA] p-4 bg-[#BFDBFE] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#1E40AF] mb-1">
                        Improvement
                      </p>
                      <h4 className="font-semibold text-[#003153]">Prediction</h4>
                      <p className="text-sm text-[#64748B]">
                        The answer or estimated result a trained model gives for new data.
                      </p>
                    </div>

                    <div className="rounded-xl border border-[#60A5FA] p-4 bg-[#BFDBFE] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#1E40AF] mb-1">
                        Improvement
                      </p>
                      <h4 className="font-semibold text-[#003153]">Overfitting</h4>
                      <p className="text-sm text-[#64748B]">
                        When a model memorizes its training data but performs poorly on unfamiliar data.
                      </p>
                    </div>

                    <div className="rounded-xl border border-[#60A5FA] p-4 bg-[#BFDBFE] hover:-translate-y-1 hover:shadow-md transition-all duration-200">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[#1E40AF] mb-1">
                        Improvement
                      </p>
                      <h4 className="font-semibold text-[#003153]">Debugging</h4>
                      <p className="text-sm text-[#64748B]">
                        Finding what is not working in your code or model and testing possible fixes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Beginner Learning Path */}
            <section
              id="learning-path"
              className="bg-gradient-to-br from-white to-[#F0F9FF] border border-[#BAE6FD] border-t-4 border-t-[#0EA5E9] rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-6 sm:p-8 scroll-mt-32"
            >
              <h2 className="text-2xl font-bold text-[#003153] mb-4">
                Beginner Machine Learning Path
              </h2>

              <p className="mb-4">
                Machine learning can feel complicated at first. Follow this
                learning path in order and focus on understanding one stage at
                a time.
              </p>

              <ol className="list-decimal ml-5 space-y-3">
                <li>
                  <strong>Learn what machine learning does.</strong> Begin by
                  understanding that a model looks for patterns in existing
                  data and uses those patterns to make predictions.
                </li>
                <li>
                  <strong>Learn how datasets are organized.</strong> Understand
                  rows, columns, features, labels, missing values, and different
                  types of data.
                </li>
                <li>
                  <strong>Choose the type of problem.</strong> Decide whether
                  you are predicting a category, predicting a number, or
                  grouping similar data.
                </li>
                <li>
                  <strong>Split your data.</strong> Use training data to teach
                  the model and testing data to measure how it performs on
                  information it has not already seen.
                </li>
                <li>
                  <strong>Run a simple model first.</strong> Begin with a
                  beginner-friendly model before trying more complicated
                  approaches.
                </li>
                <li>
                  <strong>Review more than one metric.</strong> Accuracy can be
                  useful, but precision, recall, F1 score, and loss may reveal
                  information that accuracy does not.
                </li>
                <li>
                  <strong>Change one thing at a time.</strong> Adjust your data,
                  model, or settings gradually so you can identify what caused
                  the result to change.
                </li>
                <li>
                  <strong>Record and compare results.</strong> Keep track of
                  which model and settings produced each result before deciding
                  which approach works best.
                </li>
              </ol>

              <div className="mt-5 rounded-xl bg-[#F8FAFC] border border-[#E2E8F0] p-4">
                <p className="text-sm text-[#475569]">
                  Beginner tip: A more complicated model is not automatically a
                  better model. Start with a simple approach, measure its
                  performance, and use it as a baseline for comparison.
                </p>
              </div>
            </section>

            {/* Helpful Links */}
            <section
              id="helpful-links"
              className="bg-gradient-to-br from-white to-[#F0F9FF] border border-[#BAE6FD] border-t-4 border-t-[#0EA5E9] rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-6 sm:p-8 scroll-mt-32"
            >
              <h2 className="text-2xl font-bold text-[#003153] mb-4">
                Helpful Links
              </h2>

              <p className="mb-4">
                These resources can help you learn more about machine learning
                and the tools used in DEEBug.
              </p>

              <div className="space-y-3">
                <a
                  href="https://developers.google.com/machine-learning/crash-course"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl border border-[#E2E8F0] bg-white/80 p-4 hover:border-[#0EA5E9] hover:bg-[#F0F9FF] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <h3 className="font-semibold text-[#003153]">
                    Google Machine Learning Crash Course
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    A beginner-friendly introduction to machine learning.
                  </p>
                </a>

                <a
                  href="https://www.tensorflow.org/learn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl border border-[#E2E8F0] bg-white/80 p-4 hover:border-[#0EA5E9] hover:bg-[#F0F9FF] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <h3 className="font-semibold text-[#003153]">
                    TensorFlow Learning Resources
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Guides and tutorials for learning TensorFlow.
                  </p>
                </a>

                <a
                  href="https://pytorch.org/tutorials/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl border border-[#E2E8F0] bg-white/80 p-4 hover:border-[#0EA5E9] hover:bg-[#F0F9FF] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <h3 className="font-semibold text-[#003153]">
                    PyTorch Tutorials
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Beginner and advanced tutorials for PyTorch.
                  </p>
                </a>

                <a
                  href="https://www.kaggle.com/learn/intro-to-machine-learning"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl border border-[#E2E8F0] bg-white/80 p-4 hover:border-[#0EA5E9] hover:bg-[#F0F9FF] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <h3 className="font-semibold text-[#003153]">
                    Kaggle Introduction to Machine Learning
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Short, practical lessons that explain how to build and
                    evaluate your first machine learning models.
                  </p>
                </a>

                <a
                  href="https://scikit-learn.org/stable/getting_started.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl border border-[#E2E8F0] bg-white/80 p-4 hover:border-[#0EA5E9] hover:bg-[#F0F9FF] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <h3 className="font-semibold text-[#003153]">
                    Scikit-learn Getting Started Guide
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Learn how to prepare data, train common machine learning
                    models, make predictions, and evaluate results in Python.
                  </p>
                </a>

                <a
                  href="https://pandas.pydata.org/docs/getting_started/intro_tutorials/index.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl border border-[#E2E8F0] bg-white/80 p-4 hover:border-[#0EA5E9] hover:bg-[#F0F9FF] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <h3 className="font-semibold text-[#003153]">
                    Pandas Beginner Tutorials
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Learn how to open, inspect, clean, filter, and organize
                    datasets before using them in a model.
                  </p>
                </a>

                <a
                  href="https://huggingface.co/learn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl border border-[#E2E8F0] bg-white/80 p-4 hover:border-[#0EA5E9] hover:bg-[#F0F9FF] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <h3 className="font-semibold text-[#003153]">
                    Hugging Face Learning Resources
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Explore guided courses about machine learning models,
                    datasets, natural language processing, and modern AI tools.
                  </p>
                </a>

                <a
                  href="https://developers.google.com/machine-learning/glossary"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl border border-[#E2E8F0] bg-white/80 p-4 hover:border-[#0EA5E9] hover:bg-[#F0F9FF] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <h3 className="font-semibold text-[#003153]">
                    Google Machine Learning Glossary
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Look up unfamiliar machine learning terms and read clear
                    definitions while you are learning.
                  </p>
                </a>

                <a
                  href="https://seeing-theory.brown.edu/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block rounded-xl border border-[#E2E8F0] bg-white/80 p-4 hover:border-[#0EA5E9] hover:bg-[#F0F9FF] hover:-translate-y-0.5 hover:shadow-sm transition-all duration-200"
                >
                  <h3 className="font-semibold text-[#003153]">
                    Seeing Theory
                  </h3>
                  <p className="text-sm text-[#64748B]">
                    Interactive visual lessons that make probability and
                    statistics easier to understand.
                  </p>
                </a>
              </div>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Resources;